<template>
  <div class="network-tabs vm-image-vue-tabs">
    <div class="vm-image-tabs__header">
      <div class="network-tabs-nav" role="tablist" :aria-label="ariaLabel">
        <button
          v-for="tab in tabsState"
          :key="tab.key"
          type="button"
          class="network-tab"
          :class="{ 'is-active': activeTab === tab.key }"
          :aria-selected="activeTab === tab.key ? 'true' : 'false'"
          @click="activateTab(tab.key)"
        >
          {{ tab.label }}
          <span class="network-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <p v-if="loading" class="vm-image-tabs__status">Loading…</p>
    </div>

    <div
      v-for="tab in tabsState"
      :key="`panel-${tab.key}`"
      class="network-tab-panel"
      :class="{ 'is-active': activeTab === tab.key }"
      :style="{ display: activeTab === tab.key ? '' : 'none' }"
    >
      <VmImageListResults :initial-data="tab.data" :initial-loading="!!tab.loading" />
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import VmImageListResults from './VmImageListResults.vue'

const props = defineProps({
  ariaLabel: {
    type: String,
    default: 'VM image tabs'
  },
  initialTab: {
    type: String,
    default: ''
  },
  tabs: {
    type: Array,
    default: () => []
  }
})

const activeTab = ref(props.initialTab || props.tabs[0]?.key || '')
const tabsState = ref((props.tabs || []).map((tab) => ({
  ...tab,
  data: tab.initialData || tab.data || { rows: [], shell_id: 'vm-image-detail-shell' },
  loading: false,
  loaded: false
})))
const loading = ref(false)

async function activateTab(tabKey) {
  activeTab.value = tabKey
  try {
    await loadTab(tabKey)
  } catch (error) {
    console.error('Failed to load VM image tab', error)
    window.alert('Failed to load VM images.')
  }
}

function handleUpdate(event) {
  const nextTabs = event.detail || []
  tabsState.value = tabsState.value.map((tab) => {
    const updated = nextTabs.find((entry) => entry.key === tab.key)
    if (!updated) {
      return tab
    }

    return {
      ...tab,
      count: updated.count,
      data: updated.data,
      loaded: true,
      loading: false
    }
  })
  loading.value = false
}

function handleActivateShell(event) {
  const shellId = event.detail?.shellId
  if (!shellId) {
    return
  }

  const matchingTab = tabsState.value.find((tab) => tab?.data?.shell_id === shellId)
  if (matchingTab) {
    activeTab.value = matchingTab.key
  }
}

async function loadTab(tabKey) {
  const tab = tabsState.value.find((entry) => entry.key === tabKey)
  if (!tab?.dataUrl || tab.loaded) {
    return
  }

  tab.loading = true
  loading.value = true
  const startedAt = Date.now()

  try {
    const response = await fetch(tab.dataUrl, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()
    const nextTabs = payload?.tabs_data || []
    tabsState.value = tabsState.value.map((entry) => {
      const fetched = nextTabs.find((item) => item.key === entry.key)
      if (!fetched) {
        return entry
      }

      return {
        ...entry,
        count: fetched.count,
        data: fetched.data,
        loaded: true,
        loading: false
      }
    })
  } finally {
    const elapsed = Date.now() - startedAt
    if (elapsed < 250) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 - elapsed))
    }
    loading.value = false
    const currentTab = tabsState.value.find((entry) => entry.key === tabKey)
    if (currentTab) {
      currentTab.loading = false
    }
  }
}

onMounted(() => {
  document.addEventListener('scc:vm-image-tabs:update', handleUpdate)
  document.addEventListener('scc:vm-image-tabs:activate-shell', handleActivateShell)
  activateTab(activeTab.value)
})

onBeforeUnmount(() => {
  document.removeEventListener('scc:vm-image-tabs:update', handleUpdate)
  document.removeEventListener('scc:vm-image-tabs:activate-shell', handleActivateShell)
})
</script>

<style scoped>
.vm-image-tabs__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vm-image-tabs__status {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}
</style>
